import apiHandler from '@api/ApiHandler.ts';
import { PaymentMethod } from '@app/types/payment';
import { FeedbackUser } from '@features/AdminPanelFeedbackUsers';
import { DateRange } from '@features/SettingsDemandReportUsers/model/types';
import {
  UserTemplate,
  DemandReportUser,
  InviteUserBecomeCompanyMemberRequest,
  UserActivationData,
} from '@api/user/model/types';

export interface AddPaymentMethodResponse {
  formatted_payments_methods: PaymentMethod[];
}

/**
 * Attach a Stripe payment method to the signed-in user's customer and make it
 * the customer's default card.
 *
 * Resolves with the refreshed card list, which is the whole body of the
 * backend's 200 (there is no "message" envelope: that belonged to the retired
 * /payment/ endpoint). Rejects with the axios error; the backend's reason is
 * at error.response.data.error.
 */
export const addPaymentMethod = async (
  paymentMethodId: string
): Promise<AddPaymentMethodResponse> => {
  // The response interceptor already unwraps response.data.
  const data = await apiHandler.post<AddPaymentMethodResponse>(
    `/api/case-subscriptions/payment-methods/add/`,
    { payment_method_id: paymentMethodId }
  );
  return data as unknown as AddPaymentMethodResponse;
};

/**
 * Save payment method during sign-up without authentication
 * @param email User's email
 * @param payment_method_id Stripe payment method ID
 * @param callback Success callback
 * @param onErrorCallback Error callback
 * @param onFinallyCallback Finally callback
 */
export const saveStripeInfoDuringSignup = (
  email: string,
  payment_method_id: string,
  callback: (data: any) => void,
  onErrorCallback: (error: any) => void,
  onFinallyCallback: () => void
) => {
  apiHandler
    .post<any>(`/api/case-subscriptions/payment-methods/add-signup/`, {
      email,
      payment_method_id,
    })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      onErrorCallback(error.response?.data || error);
    })
    .finally(() => {
      onFinallyCallback();
    });
};

export const updateDefaultMethod = (
  payment_method_id: string,
  callback: (data: any) => void
) => {
  apiHandler
    .post<any>(`/api/case-subscriptions/payment-methods/set-default/`, {
      payment_method_id,
    })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback([]);
    });
};

export const deletePaymentMethod = (
  payment_method_id: string,
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .post<any>(`/api/case-subscriptions/payment-methods/delete/`, {
      payment_method_id,
    })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error as any);
    });
};

export const getUserUsage = (
  callback: (data: any) => void,
  errorCallback: (data: any) => void
) => {
  apiHandler
    .get<any>(`/user/get_user_generation_data/`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error as any);
    });
};

export const runManualPayment = (
  callback: (data: any) => void,
  errorCallback: (data: any) => void,
  finallyCallback: () => void
) => {
  apiHandler
    .post<any>(`/api/case-subscriptions/run-manual-payment/`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error as any);
    })
    .finally(() => {
      finallyCallback();
    });
};

export const setDefaultTemplate = async (
  pk: string | number,
  callback: (template: UserTemplate) => void
) => {
  try {
    const response = await apiHandler.patch<UserTemplate>(
      `/user_templates/${pk}/`,
      {
        default: true,
      }
    );
    callback(response as any);
  } catch (error) {
    console.error('Error:', error);
    callback({} as any);
  }
};

export const getUserTemplates = (callback: (data: any) => void) => {
  apiHandler
    .get<UserTemplate[]>('/user_templates/')
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.log('Error:', error);
      callback({});
    });
};

export const deleteUserTemplate = (
  id: number,
  callback: (message: string) => void
) => {
  apiHandler
    .delete(`/user_templates/${id}/`)
    .then(() => {
      callback('Success');
    })
    .catch((error) => {
      callback(error);
    });
};

export const getFeedbackUsers = (callback: (data: any) => void) => {
  apiHandler
    .get<FeedbackUser[]>(`/feedbacks/`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback(error);
    });
};

export const getDemandReportUsers = (
  params: DateRange,
  callback: (data: any) => void
) => {
  apiHandler
    .get<DemandReportUser[]>(`/created-docs-report/`, { params })
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      console.error('Error:', error);
      callback(error);
    });
};

export const inviteUserBecomeCompanyMember = (
  data: InviteUserBecomeCompanyMemberRequest,
  callback: (data: any) => void,
  errorCallback: (error: any) => void
) => {
  apiHandler
    .post(`/invite_company_member/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const activateUser = (
  data: any,
  callback: (data: any) => void,
  errorCallback: (error: any) => void
) => {
  apiHandler
    .post(`/activate_account/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const getUserActivationData = (
  token: any,
  callback: (data: UserActivationData) => void,
  errorCallback: (error: any) => void
) => {
  apiHandler
    .get('user/get_user_activation_data/', { params: { token } })
    .then((response) => {
      callback(response as any as UserActivationData);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const deleteCompanyMember = (
  member_id: number,
  callback: (data: any) => void,
  errorCallback: (error: any) => void
) => {
  apiHandler
    .delete(`/delete_company_member/${member_id}/`)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error.response);
    });
};

export const updateAdminRoleForCompanyMember = (
  member_id: number,
  data: any,
  callback: (data: any) => void,
  errorCallback: (error: any) => void
) => {
  apiHandler
    .patch(`/change_member_admin_role/${member_id}/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};

export const activateCompanyUserAccountWithAdmin = (
  member_id: number,
  data: any,
  callback: (data: any) => void,
  errorCallback: (error: any) => void
) => {
  apiHandler
    .patch(`/change_company_account_status/${member_id}/`, data)
    .then((response) => {
      callback(response as any);
    })
    .catch((error) => {
      errorCallback(error);
    });
};
